
import React from "react";
import { reportClientError } from "../utils/clientErrorReporter";

const CHUNK_RELOAD_KEY = "uniflo:chunk-reload-attempted";
const CHUNK_RELOAD_TTL_MS = 5 * 60 * 1000;

function isChunkLoadError(error) {
  const name = `${error?.name || ""}`;
  const message = `${error?.message || error || ""}`;
  return (
    name === "ChunkLoadError" ||
    /loading chunk \d+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message)
  );
}

function shouldReloadForChunkError(error) {
  if (!isChunkLoadError(error) || typeof window === "undefined") return false;

  try {
    const raw = window.sessionStorage?.getItem(CHUNK_RELOAD_KEY);
    const previous = raw ? JSON.parse(raw) : null;
    const now = Date.now();
    const sameRoute =
      previous?.path === `${window.location.pathname}${window.location.search}`;
    const recent = Number(previous?.at || 0) > now - CHUNK_RELOAD_TTL_MS;

    if (sameRoute && recent) return false;

    window.sessionStorage?.setItem(
      CHUNK_RELOAD_KEY,
      JSON.stringify({
        at: now,
        path: `${window.location.pathname}${window.location.search}`,
      })
    );
    return true;
  } catch (_error) {
    return false;
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error(error, errorInfo);
    if (shouldReloadForChunkError(error)) {
      window.location.reload();
      return;
    }

    reportClientError({
      kind: "react_render_error",
      source: "react_error_boundary",
      handled: true,
      error,
      componentStack: errorInfo?.componentStack || "",
      metadata: {
        boundary: this.constructor?.name || "ErrorBoundary",
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <h1>Something went wrong.</h1>
          <button
            className="btn_blue"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
